import { inject, Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, filter, switchMap, take, tap, throwError } from 'rxjs';
import { FetchOptionsStateModel } from '../../../core/state/fetchOptions.state';
import { ExplanationContentJsonModel } from '../../content/explanation-content.json.model';
import { ExplanationContentService } from '../../content/explanation-content.service';
import { ExplanationContentActions } from './explanation-content.actions';
import { LearningSectionState } from '../../../learning-section/ngxs/learning-section/learning-section.state';
import { LearningSectionJsonModel } from '../../../learning-section/learning-section.json.model';
import { map } from 'rxjs/operators';
import { IRawModel } from '@datx/utils';
import { LearningSectionActions } from '../../../learning-section/ngxs/learning-section/learning-section.actions';
import {
  LearningSectionContainerState,
  LearningSectionContainerStateModel
} from '../../../learning-section/ngxs/learning-section-container/learning-section-container.state';
import {
  LearningSectionContainerActions
} from '../../../learning-section/ngxs/learning-section-container/learning-section-container.actions';

export interface ExplanationContentStateModel {
  explanationContents: ExplanationContentJsonModel[]
  explanationContent: ExplanationContentJsonModel | null
}

@State<ExplanationContentStateModel>({
  name: 'explanationContents',
  defaults: {
    explanationContents: [],
    explanationContent: null
  }
})
@Injectable()
export class ExplanationContentState {
  private readonly explanationContentService = inject(ExplanationContentService);
  private readonly store = inject(Store);

  @Selector()
  static getState(state: ExplanationContentStateModel) {
    return state;
  }

  @Selector()
  static getExplanationContents(state: ExplanationContentStateModel) {
    return state.explanationContents;
  }

  @Selector()
  static getExplanationContent(state: ExplanationContentStateModel) {
    return state.explanationContent;
  }

  @Selector()
  static getById(id: string) {
    return createSelector(
      [ExplanationContentState],
      (state: ExplanationContentStateModel) => {
        return state.explanationContents.find(content => content.id === id) ?? null;
      }
    );
  }

  @Action(ExplanationContentActions.Fetch)
  onFetchExplanationContents(ctx: StateContext<ExplanationContentStateModel>, action: ExplanationContentActions.Fetch) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(
      state => state.fetchOptions
    );

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides
    };

    return this.explanationContentService.getAllWithPagination({}, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new ExplanationContentActions.FetchSuccess(res[0]));
      })
    );
  }

  @Action(ExplanationContentActions.FetchSuccess)
  onFetchExplanationContentsSuccess(ctx: StateContext<ExplanationContentStateModel>, { payload }: ExplanationContentActions.FetchSuccess) {
    ctx.setState({ ...ctx.getState(), explanationContents: payload });
  }

  @Action(ExplanationContentActions.FetchOne)
  onFetchOneExplanation(ctx: StateContext<ExplanationContentStateModel>, action: ExplanationContentActions.FetchOne) {
    return this.explanationContentService.getOneWithId(action.id, {}).pipe(
      tap((res: ExplanationContentJsonModel) => {
        ctx.dispatch(new ExplanationContentActions.FetchOneSuccess(res));
      })
    );
  }

  @Action(ExplanationContentActions.FetchOneSuccess)
  onFetchOneSuccess(ctx: StateContext<ExplanationContentStateModel>, { payload }: ExplanationContentActions.FetchOneSuccess) {
    ctx.setState({ ...ctx.getState(), explanationContent: payload });
  }

  @Action(ExplanationContentActions.FetchAppend)
  onFetchAppendExplanationContents(ctx: StateContext<ExplanationContentStateModel>, action: ExplanationContentActions.Fetch) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides
    };

    return this.explanationContentService.getAllWithPagination({}, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new ExplanationContentActions.FetchSuccessAppend(res[0]));
      })
    );
  }

  @Action(ExplanationContentActions.FetchSuccessAppend)
  onFetchExplanationContentsSuccessAppend(ctx: StateContext<ExplanationContentStateModel>, { payload }: ExplanationContentActions.FetchSuccess) {
    const state = ctx.getState();
    ctx.setState({
      ...ctx.getState(),
      explanationContents: [...state.explanationContents, ...payload]
    });
  }

  @Action(ExplanationContentActions.AddRaw)
  onAddRaw(ctx: StateContext<ExplanationContentStateModel>, { payload }: ExplanationContentActions.AddRaw) {
    return this.store.select(ExplanationContentState.getExplanationContent).pipe(
      take(1),
      map(() => {
        const rawModel: IRawModel = {
          type: 'explanation-contents',
          attributes: {
            name: payload.name,
            content: payload.content
          }
        };
        return { rawModel };
      }),
      switchMap(({ rawModel }) =>
        this.explanationContentService.createAndSave(rawModel).pipe(
          switchMap((createdContent: ExplanationContentJsonModel) =>
            this.store.select(LearningSectionContainerState.getLearningSectionContainer).pipe(
              filter(container => !!container),
              take(1),
              tap(container => {
                container.content = createdContent;
                ctx.dispatch(new LearningSectionContainerActions.Update(container));
              }),
              tap(() => {
                ctx.dispatch(new ExplanationContentActions.FetchOne(createdContent.id));
              }),
              map(() => createdContent)
            )
          ),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  @Action(ExplanationContentActions.EditRaw)
  onEditRaw(ctx: StateContext<LearningSectionContainerStateModel>, { payload }: ExplanationContentActions.EditRaw) {
    return this.explanationContentService.update(payload).pipe(
      tap(() => {
        // Dispatch FetchOne for the updated ExplanationContent
        ctx.dispatch(new ExplanationContentActions.FetchOne(payload.id));

        // Select LearningSection and dispatch FetchOne if it exists
        this.store.select(LearningSectionState.getLearningSection).pipe(
          filter((section): section is LearningSectionJsonModel => !!section),
          take(1),
          tap((section) => ctx.dispatch(new LearningSectionActions.FetchOne(section.id)))
        ).subscribe();
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
